"use client"

import React, { useState, useEffect, useCallback } from "react"
import { TextField, Autocomplete, CircularProgress, Paper, Box, Chip } from "@mui/material"
import { debounce } from "lodash"
import { fetchPlansV2 } from "./providers-api"

const PlanAutocomplete = ({ value, onChange, organizationId, organizationName = "", disabled, error, readOnly = false, initialValue = null }) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [isReadOnly, setIsReadOnly] = useState(readOnly)

  const loadOptions = useCallback(
    async (searchTerm = "", pageNum = 1, append = false) => {
      if (!organizationId || (!hasMore && append)) return

      setLoading(true)
      try {
        const response = await fetchPlansV2({
          organizationId,
          organizationName,
          search: searchTerm,
          page: pageNum,
          limit: 20,
        })

        const newOptions = response.plans || []
        setOptions((prev) => (append ? [...prev, ...newOptions] : newOptions))
        setHasMore(response.hasMore || false)
        setPage(pageNum)
      } catch (error) {
        console.error("Error loading plans:", error)
      } finally {
        setLoading(false)
      }
    },
    [organizationId, organizationName, hasMore],
  )

  const debouncedLoadOptions = useCallback(
    debounce((searchTerm) => {
      setPage(1)
      setHasMore(true)
      loadOptions(searchTerm, 1, false)
    }, 1200), // Increased to 1.2 seconds
    [loadOptions],
  )

  useEffect(() => {
    // If initialValue is provided, set it and make it read-only
    if (initialValue) {
      onChange(initialValue)
      setIsReadOnly(true)
    } else if (organizationId) {
      setOptions([])
      setPage(1)
      setHasMore(true)
      loadOptions("", 1, false)
    } else {
      setOptions([])
      onChange(null)
    }
  }, [organizationId, initialValue])

  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue)
    
    // Don't trigger API call when user selects an option from dropdown
    // reason can be: 'input' (user typing), 'reset' (selection/clear), 'clear' (clear button)
    if (reason === 'reset' || reason === 'clear') {
      return
    }
    
    // Only trigger API call when user is actually typing
    if (organizationId && reason === 'input') {
      debouncedLoadOptions(newInputValue)
    }
  }

  const handleChange = (event, newValue) => {
    onChange(newValue)
  }

  const handleScroll = (event) => {
    const { target } = event
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5 && hasMore && !loading) {
      loadOptions(inputValue, page + 1, true)
    }
  }

  // If read-only (auto-populated), show as chip in a disabled field
  if (isReadOnly && value) {
    return (
      <Box
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: "6px",
          height: "49px",
          px: 2,
          display: "flex",
          alignItems: "center",
          cursor: "not-allowed",
          "&:hover": {
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
        }}
      >
        <Chip
          label={value.name}
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            height: "30px",
            maxWidth: "100%",
          }}
        />
      </Box>
    )
  }

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      loading={loading}
      disabled={disabled}
      PaperComponent={(props) => (
        <Paper
          {...props}
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}
        />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={disabled ? "Select organization first" : "Select plan"}
          variant="outlined"
          fullWidth
          error={error}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2",
                  borderWidth: 2,
                },
              },
              ...(error && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d32f2f",
                  borderWidth: 2,
                },
              }),
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: 200 },
      }}
      noOptionsText={disabled ? "Select organization first" : inputValue ? "No plans found" : "Start typing to search"}
    />
  )
}

export default PlanAutocomplete
